// ══════════════════════════════════════════════════════════════════════════════════════════════
// ÉLAGUER LE JOURNAL DES BASES MAGASIN — le geste que la route du back-office attendait.
//
// POURQUOI CET ÉCRAN EXISTE. La route `purge-journal` porte depuis le début : « déclenchée, jamais
// automatique à ce stade : on veut lire ses rapports avant de la mettre en tâche de fond ». La
// décision est bonne — mais son étape 1 n'avait jamais été construite : ni bouton, ni commande, ni
// fonction dans le client du back-office. Les rapports qu'on voulait lire, personne ne les a lus, et
// le journal `event` pèse 56 % de la plus grosse base de la bêta.
//
// ⚠️ 🔴 LES DEUX NOMBRES SONT TROMPEURS SANS LEUR PHRASE, et c'est la moitié du travail de cet écran.
// « Conservation 30 jours » ne veut PAS dire « on efface ce qui a plus de 30 jours » : la purge
// n'efface que ce que TOUTES les caisses actives ont déjà lu, et les deux durées ne font que
// restreindre davantage. Un magasin dont une tablette est éteinte depuis trois semaines ne verra rien
// s'effacer, quel que soit le réglage — sans cette phrase, « 0 purgé » se lit comme une panne.
//
// ⚠️ LES REFUS SE MONTRENT, ET CE NE SONT PAS DES ERREURS. Ce sont les canaux que la purge s'est
// INTERDIT de toucher (fenêtre de l'historique fiscal, amorçage d'une caisse neuve, canal qu'aucune
// caisse active n'a jamais tiré). Les cacher ferait lire un rapport à zéro comme « rien à faire ».
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { confirm, EmptyState, showToast } from '@orgazone/ui-web';
import { api, type MigrationStoreState, type PurgeJournalReport } from '../api';
import { Spin } from '../ui';

/** Les défauts du back-office, repris ICI pour que l'écran annonce ce qu'il enverra vraiment. */
const RETENTION_DEFAULT = 30;
const FORGET_DEFAULT = 90;

/**
 * Groupe les milliers d'un compte — « 403 508 » plutôt que « 403508 ».
 *
 * ⚠️ AUCUNE ÉTIQUETTE DE LANGUE ÉCRITE ICI, délibérément : une étiquette en dur déciderait du format
 * pour TOUT LE MONDE (§10 du cadrage des référentiels), et cet écran n'appartient à aucun magasin —
 * c'est une surface d'exploitant, donc le navigateur décide, comme sur l'écran de connexion. Le jour
 * où la Console reçoit sa convention de format, ce point unique la reçoit avec elle.
 */
const groupDigits = (n: number): string => n.toLocaleString();

const fieldStyle = {
  height: 30, width: 74, padding: '0 8px', borderRadius: 'var(--r-xs)',
  border: '1px solid var(--ink200)', background: 'var(--paper)', color: 'var(--ink900)',
  fontSize: 'var(--t-dense)', fontFamily: 'var(--font-mono)',
} as const;

const note = { fontSize: 'var(--t-sub)', color: 'var(--ink500)', lineHeight: 1.5 } as const;

export function PurgeJournalCard({ stores, canRun }: { stores: MigrationStoreState[]; canRun: boolean }) {
  const [retention, setRetention] = useState(String(RETENTION_DEFAULT));
  const [forget, setForget] = useState(String(FORGET_DEFAULT));
  const [busy, setBusy] = useState(false);
  const [rapport, setRapport] = useState<PurgeJournalReport | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  /**
   * ⚠️ LES BORNES SONT VÉRIFIÉES ICI *ET* AU SERVEUR. Ici pour corriger tout de suite ; au serveur
   * parce qu'il ne se fie jamais à ce qu'on lui envoie — et parce qu'une valeur refusée doit l'être
   * plutôt que rabattue en silence sur un défaut que l'écran n'affiche pas.
   */
  const n = (v: string) => (/^\d+$/.test(v.trim()) ? Number(v.trim()) : NaN);
  const c = n(retention);
  const o = n(forget);
  const inputRejected = !Number.isFinite(c) || c < 1 || c > 3650 ? 'La conservation minimale doit être un nombre de jours entre 1 et 3650.'
    : !Number.isFinite(o) || o < 1 || o > 3650 ? 'L’oubli d’une caisse muette doit être un nombre de jours entre 1 et 3650.'
    // ⚠️ Oublier une caisse plus tôt qu'on ne conserve n'a pas de sens : on cesserait de l'attendre
    //    avant même d'avoir le droit d'effacer ce qu'elle n'a pas lu.
    : o < c ? `L’oubli (${o} j) ne peut pas être plus court que la conservation (${c} j).`
    : null;

  const run = async () => {
    if (inputRejected || !stores.length) return;
    const ok = await confirm({
      title: `Élaguer le journal de ${stores.length} base(s) ?`,
      message: `Seuls les évènements que TOUTES les caisses actives ont déjà lus seront retirés, et rien de plus récent que ${c} jours. `
        + `Une caisse sans nouvelles depuis plus de ${o} jours cesse de retenir le journal : elle rechargera tout à son retour.`,
      confirmLabel: 'Élaguer',
      // ⚠️ `info` ET NON `danger` : la purge ne retire que ce qui a été lu par tout le monde, et ses
      //    trois garde-fous (plancher purgé, plancher temporel, oubli des muettes) la rendent sûre
      //    par construction. Peindre en rouge un geste sûr use l'avertissement des gestes qui le sont.
      tone: 'info',
    });
    if (!ok) return;
    setBusy(true); setErreur(null);
    try {
      const r = await api.migrationsPurgeJournal(stores.map((s) => s.gln), c, o);
      setRapport(r);
      const dbs = stores.length > 1 ? `${stores.length} bases` : '1 base';
      showToast(`${groupDigits(r.totalRemoved)} évènements retirés sur ${dbs}.`);
    } catch (e) {
      // §23.0 : le motif RÉEL du serveur, jamais une phrase rassurante.
      setErreur((e as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, flex: 1 }}>Journal des bases</span>
        <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{stores.length}</span>
      </div>
      <div style={{ padding: '10px 16px 12px' }}>
        {/* ⚠️ CE QUE LA PURGE FAIT VRAIMENT, EN PREMIER. Sans cette phrase, les deux nombres
            ci-dessous se lisent « on efface ce qui a plus de 30 jours » — ce qui est faux. */}
        <div style={{ ...note, marginBottom: 10 }}>
          Retire du journal <strong>ce que toutes les caisses actives ont déjà lu</strong>. Un magasin
          dont une tablette est éteinte ne verra rien s’effacer, quel que soit le réglage.
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <input inputMode="numeric" value={retention} onChange={(e) => setRetention(e.target.value)} style={fieldStyle} aria-label="Conservation minimale, en jours" />
          <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600 }}>jours conservés quoi qu’il arrive</span>
        </label>
        <div style={{ ...note, marginBottom: 10 }}>
          Même lu par toutes les caisses, rien de plus récent n’est touché — c’est ce qui permet de
          <strong> diagnostiquer</strong> après un incident.
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <input inputMode="numeric" value={forget} onChange={(e) => setForget(e.target.value)} style={fieldStyle} aria-label="Oubli d’une caisse muette, en jours" />
          <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600 }}>jours avant d’oublier une caisse muette</span>
        </label>
        {/* ⚠️ LA CONSÉQUENCE VISIBLE SE DIT : c'est le seul des deux nombres dont l'effet se constate
            au comptoir, et une tablette de secours rangée dans un tiroir dépasse 90 jours. */}
        <div style={{ ...note, marginBottom: 10 }}>
          Passé ce délai, elle cesse de retenir le journal. Rien n’est perdu — mais elle
          <strong> rechargera tout</strong> à son retour (tablette de secours, magasin saisonnier).
        </div>

        {inputRejected && <div style={{ fontSize: 'var(--t-meta)', color: 'var(--da-fg)', lineHeight: 1.5, marginBottom: 8 }}>{inputRejected}</div>}

        {canRun ? (
          <button type="button" onClick={() => void run()} disabled={busy || !!inputRejected || stores.length === 0}
            className="btn-ghost" style={{ height: 28, padding: '0 11px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-sub)', fontWeight: 600 }}>
            {busy ? <><Spin size={11} /> Élagage…</> : `Élaguer ${stores.length} base(s)`}
          </button>
        ) : (
          <div style={note}>Réservé aux comptes qui peuvent lancer une migration.</div>
        )}

        {erreur && <div style={{ fontSize: 'var(--t-meta)', color: 'var(--da-fg)', lineHeight: 1.5, marginTop: 8 }}>Élagage impossible : {erreur}</div>}

        {rapport && (
          <div style={{ marginTop: 10, borderTop: '1px solid var(--ink100)', paddingTop: 8 }}>
            {rapport.results.length === 0
              ? <EmptyState compact icon="history" title="Aucune base traitée" note="Le périmètre courant ne contient aucun magasin." />
              : rapport.results.map((r) => (
                <div key={r.gln} style={{ padding: '7px 0', borderBottom: '1px solid var(--ink100)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span className="mono" style={{ fontSize: 'var(--t-sub)', fontWeight: 600, flex: 1, minWidth: 90 }}>
                      {stores.find((s) => s.gln === r.gln)?.name ?? r.gln}
                    </span>
                    <span className="mono" style={{ fontSize: 'var(--t-sub)', color: r.ok ? 'var(--ok-fg)' : 'var(--da-fg)', whiteSpace: 'nowrap' }}>
                      {r.ok ? `${groupDigits(r.totalRemoved ?? 0)} retirés` : 'échec'}
                    </span>
                  </div>
                  {r.error && <div style={{ fontSize: 'var(--t-micro)', color: 'var(--da-fg)', marginTop: 2, lineHeight: 1.5 }}>{r.error}</div>}
                  {/* ⚠️ Les caisses ignorées se comptent : une purge qui avance parce qu'on a cessé
                      d'attendre une caisse n'est pas la même chose qu'une purge où tout le monde est à jour. */}
                  {!!r.ignoredDevices && (
                    <div style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)', marginTop: 2 }}>
                      {r.ignoredDevices} caisse(s) muette(s) ignorée(s) — elles rechargeront tout à leur retour.
                    </div>
                  )}
                  {/* ⚠️ 🔴 LES REFUS NE SONT PAS DES ERREURS : ce sont des gardes qui ont fait leur
                      travail. Les taire ferait lire « 0 retiré » comme une panne. */}
                  {r.refused?.map((x) => (
                    <div key={x.channel} style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)', marginTop: 2, lineHeight: 1.5 }}>
                      <span className="mono" style={{ color: 'var(--wa-fg)' }}>{x.channel}</span> non purgé — {x.reason}
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}

        {/* ⚠️ L'AUTOMATIQUE N'EXISTE PAS, ET L'ÉCRAN LE DIT plutôt que de laisser croire à un réglage
            caché. C'est une étape assumée : on lit les rapports avant de planifier un effacement. */}
        <div style={{ ...note, marginTop: 8 }}>
          Aucun élagage automatique : il se déclenche d’ici, et seulement d’ici. La mise en tâche de
          fond viendra quand ces rapports n’auront plus de surprise.
        </div>
      </div>
    </div>
  );
}
